import { Decimal } from 'decimal.js';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowageStatusEnum } from 'src/enums/stowage/stowageStatusEnum';
import { roundTo0Decimal } from 'src/utils/numberHelper';
import { getCargoOperationWeight, isLoadingOrUnloadingCargoOperation } from '../cargoOperationUtils';
import { getSwingValues } from '../cargoOptionUtils';
import { calculateVolume } from '../stowageOperation/volumeUtils';
import { getCargoTemperature } from '../stowageOperation/cargoTemperatureUtils';
import { calculateUnloadingDifference } from '../stowageOperation/operationDisplayUtil';
import { hasZeroStowedWeight } from '../stowageOperation/rotationCallStatusesUtils';
import { notStowedWeightInTanks } from '../stowageOperation/stowageOperationUtils';
import { STOWAGE_INPUT_TOLERANCE } from '../stowagePrecision';
import { getTankContentsInPortCall } from '../manifest/cargoManifestUtils';

const NA = 'N/A';

// ===========================
// SWING VALUE FORMATTERS
// ===========================

/**
 * Get formatted total swing weights for a cargo operation
 * Format: "{nominated} / [{min}, {max}] MT"
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted swing weights string or empty string for non-loading/unloading operations
 */
export const getTotalSwingWeights = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    if (!isLoadingOrUnloadingCargoOperation(cargoOperation)) {
        return '';
    }
    const sign = cargoOperation.operationType === 'Unloading' ? -1 : 1;
    const format = (weight: number) => roundTo0Decimal(sign * weight);

    const nominatedWeight = getCargoOperationWeight(activeStowagePlan, cargoOperation.cargo, 'Loading', 'nominated');
    const contractWeight = getCargoOperationWeight(activeStowagePlan, cargoOperation.cargo, 'Loading', 'contract');
    const swingValues = getSwingValues(contractWeight, cargoOperation.cargo);
    return `${nominatedWeight ? format(nominatedWeight) : NA} / [${swingValues.minWeight ? format(swingValues.minWeight) : NA}, ${
        swingValues.maxWeight ? format(swingValues.maxWeight) : NA
    }] MT`;
};

/**
 * Get formatted total swing volumes for a cargo operation
 * Format: "{nominated} / [{min}, {max}] m³"
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Formatted swing volumes string or empty string for non-loading/unloading operations
 */
export const getTotalSwingVolumes = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    if (!isLoadingOrUnloadingCargoOperation(cargoOperation)) {
        return '';
    }
    const sign = cargoOperation.operationType === 'Unloading' ? -1 : 1;
    const format = (weight: number) =>
        roundTo0Decimal(
            sign * calculateVolume(new Decimal(weight), cargoOperation.cargo, getCargoTemperature(activeStowagePlan, cargoOperation.cargo)).toNumber()
        );

    const nominatedWeight = getCargoOperationWeight(activeStowagePlan, cargoOperation.cargo, 'Loading', 'nominated');
    const contractWeight = getCargoOperationWeight(activeStowagePlan, cargoOperation.cargo, 'Loading', 'contract');
    const swingValues = getSwingValues(contractWeight, cargoOperation.cargo);
    return `${nominatedWeight ? format(nominatedWeight) : NA} / [${swingValues.minWeight ? format(swingValues.minWeight) : NA}, ${
        swingValues.maxWeight ? format(swingValues.maxWeight) : NA
    }] m³`;
};

// ===========================
// OPERATION STATUS UTILITIES
// ===========================

/**
 * Check if unloading operation has remaining cargo to unload
 * Wrapper around existing calculateUnloadingDifference utility
 * @param stowagePlan - The active stowage plan
 * @param unloadingCargoOperation - The unloading cargo operation
 * @returns True if there is remaining cargo to unload
 */
export const hasRemainingCargoToUnload = (stowagePlan: StowagePlan, unloadingCargoOperation: RotationCargoOperation): boolean => {
    const difference = calculateUnloadingDifference(stowagePlan, unloadingCargoOperation);
    return difference.gt(0);
};

/**
 * Get the stowage status for a cargo operation
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns The stowage status enum value
 */
export const getOperationStowageStatus = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): StowageStatusEnum => {
    let stowageStatus = StowageStatusEnum.Stowed;

    if (hasZeroStowedWeight(activeStowagePlan, cargoOperation)) {
        stowageStatus = StowageStatusEnum.MissingStowage;
    } else if (
        cargoOperation.operationType === 'Loading' &&
        notStowedWeightInTanks(activeStowagePlan, cargoOperation).lessThan(STOWAGE_INPUT_TOLERANCE.negated())
    ) {
        stowageStatus = StowageStatusEnum.InvalidStowage;
    } else if (cargoOperation.operationType === 'Unloading' && hasRemainingCargoToUnload(activeStowagePlan, cargoOperation)) {
        stowageStatus = StowageStatusEnum.DifferenceStowedUnstowedStowage;
    }

    return stowageStatus;
};

// ===========================
// SELECTION UTILITIES
// ===========================

/**
 * Parameters for determining selected operation list item class
 */
export interface SelectionParams {
    selectedCargoOperation: RotationCargoOperation | null;
    activeStowagePlan: StowagePlan;
    selectedTanks: Tank[];
    selectedStowageRotationPortCall: RotationPortCall;
    cargoOperation: RotationCargoOperation;
}

/**
 * Get the CSS class for selected operation list item
 * Returns 'selected-operation' if the operation is selected directly or if any selected tank contains the cargo
 * @param params - Parameters containing selection state and cargo operation
 * @returns CSS class string ('selected-operation' or empty string)
 */
export const getSelectedOperationListItemClass = (params: SelectionParams): string => {
    const { selectedCargoOperation, activeStowagePlan, selectedTanks, selectedStowageRotationPortCall, cargoOperation } = params;

    if (selectedCargoOperation && selectedCargoOperation.cargoOperationGuid === cargoOperation.cargoOperationGuid) {
        return 'selected-operation';
    }

    if (!activeStowagePlan || !selectedTanks?.length) {
        return '';
    }

    for (const tank of selectedTanks) {
        const _cargoTanksInPortCall = getTankContentsInPortCall(activeStowagePlan, selectedStowageRotationPortCall, tank);

        for (const t of _cargoTanksInPortCall) {
            for (const c of t.cargoContents) {
                if (c?.stowage?.cargoOperation?.cargoGuid === cargoOperation.cargoGuid) {
                    return 'selected-operation';
                }
            }
        }
    }

    return '';
};

// ===========================
// MENU POSITION UTILITIES
// ===========================

// Constants for menu positioning
const MENU_PANEL_HEIGHT = 340;
const PAGE_HEADER_OFFSET = 80;
const SCREEN_LARGE_WIDTH_THRESHOLD = 3000;

/**
 * Parameters for calculating menu Y position
 */
export interface MenuPositionParams {
    clientY: number;
    windowHeight: number;
    windowWidth: number;
    tankPlanWindowResized: string;
    popOutOperationList: boolean;
    portalRootHeight: number;
}

/**
 * Calculate the Y position for the operations context menu
 * Ensures menu stays within viewport bounds
 * @param params - Parameters containing mouse event position and window dimensions
 * @returns Calculated Y position for the menu
 */
export const calculateMenuYPosition = (params: MenuPositionParams): number => {
    const { clientY, windowHeight, windowWidth, tankPlanWindowResized, popOutOperationList, portalRootHeight } = params;

    const isLargeScreen = windowWidth >= SCREEN_LARGE_WIDTH_THRESHOLD;

    let operationsMenuYpos = 0;
    if (isLargeScreen || popOutOperationList) {
        operationsMenuYpos = PAGE_HEADER_OFFSET;
        const operationsPanelYpos = PAGE_HEADER_OFFSET;
        const offsetItemClickedToPanelTop = clientY - operationsPanelYpos;
        operationsMenuYpos = offsetItemClickedToPanelTop;

        if (clientY + MENU_PANEL_HEIGHT > portalRootHeight) {
            const adjustYPos = clientY + MENU_PANEL_HEIGHT > portalRootHeight ? clientY + MENU_PANEL_HEIGHT - portalRootHeight : 0;
            operationsMenuYpos = operationsMenuYpos - adjustYPos;
        }
    } else {
        let operationsPanelYpos = Number(tankPlanWindowResized.split('fr')[0]) * (windowHeight - PAGE_HEADER_OFFSET);
        operationsPanelYpos += PAGE_HEADER_OFFSET;
        const offsetItemClickedToPanelTop = clientY - operationsPanelYpos;
        operationsMenuYpos = offsetItemClickedToPanelTop;

        if (clientY + MENU_PANEL_HEIGHT > windowHeight) {
            const adjustYPos = clientY + MENU_PANEL_HEIGHT > windowHeight ? clientY + MENU_PANEL_HEIGHT - windowHeight : 0;
            operationsMenuYpos = operationsMenuYpos - adjustYPos;
        }
    }

    return operationsMenuYpos;
};
